import { useMutation } from '@tanstack/react-query'
import { CreditCard, Loader2 } from 'lucide-react'
import { useTranslation } from 'react-i18next'
import { Link } from 'react-router-dom'
import { LESSON_PRICE, SURVEY_BONUS_CREDITS } from '@superlekcja/shared'
import { Button } from '@/components/ui/button'
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { CREDIT_PACKAGES, creditsToPln } from '@/config/credit-packages'
import { useAuth } from '@/contexts/AuthContext'
import { track } from '@/utils/analytics'
import { CREDITS_SURVEY_PATH } from './credits-survey-page'
import { recordTopupIntent } from './survey.service'

/*
  Fake paygate (TASK-101.8, spec 2026-10-12-fake-paygate-doladowania-design.md). PayU jest
  wylaczony, a eksperyment mierzy, czy nauczyciel kliknie w KONKRETNA cene — dlatego cena stoi
  na przycisku. Klik zapisuje backend (niezaleznie od zgody na cookies), po nim mowimy prawde:
  platnosci jeszcze nie dzialaja. Ankieta za kredyty jest nagroda tylko, gdy konto moze ja
  wypelnic ('eligible': raz na konto, saldo ponizej ceny lekcji).
*/
const OFFER_CREDITS = CREDIT_PACKAGES[0]

export function TopupOffer({ onNavigate }: { onNavigate: () => void }) {
  const { t } = useTranslation()
  const { profile } = useAuth()
  const intent = useMutation({ mutationFn: recordTopupIntent })
  const bonus = { bonus: SURVEY_BONUS_CREDITS, lessons: SURVEY_BONUS_CREDITS / LESSON_PRICE }

  const onClick = () => {
    track('topup_clicked', { exhausted: (profile?.credits ?? 0) < LESSON_PRICE })
    intent.mutate()
  }

  return (
    <>
      <Button
        variant="outline"
        data-testid="topup-button"
        onClick={onClick}
        className="mt-4 h-auto w-full justify-between py-3"
      >
        <span className="flex items-center gap-2">
          <CreditCard className="h-4 w-4" />
          {t('credits-survey.topup-button')}
        </span>
        <span className="font-bold">
          {t('credits-survey.topup-price', { credits: OFFER_CREDITS, pln: creditsToPln(OFFER_CREDITS) })}
        </span>
      </Button>

      <Dialog open={!intent.isIdle} onOpenChange={open => { if (!open) intent.reset() }}>
        <DialogContent data-testid="topup-dialog">
          <DialogHeader>
            <DialogTitle>{t('credits-survey.topup-title')}</DialogTitle>
            <DialogDescription>{t('credits-survey.topup-testing')}</DialogDescription>
          </DialogHeader>
          {intent.isPending && <Loader2 className="h-5 w-5 animate-spin" />}
          {/* Glosno: bez zapisu nie wiemy, czy wolno zaprosic do ankiety. */}
          {intent.isError && <p className="text-destructive" role="alert" data-testid="topup-error">{t('credits-survey.topup-failed')}</p>}
          {intent.data?.surveyStatus === 'eligible' && (
            <Button asChild data-testid="topup-survey-link">
              <Link to={CREDITS_SURVEY_PATH} onClick={() => { intent.reset(); onNavigate() }}>
                {t('credits-survey.topup-survey', bonus)}
              </Link>
            </Button>
          )}
          {intent.data && intent.data.surveyStatus !== 'eligible' && (
            <p data-testid="topup-payments-soon">{t('credits-survey.topup-payments-soon')}</p>
          )}
        </DialogContent>
      </Dialog>
    </>
  )
}
